import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useCallback, useMemo, useState } from "react";

import {
  updateProperty,
  clearMessage,
} from "@/services/productServices/productActions";

import UpdatePropertiesView from "./UpdatePropertiesView";

/**
 * One per-site flag on a product property (show / box), keyed by site id: an
 * ObjectId in the server typedef, a string over JSON, so compared, never read.
 */
type SiteFlag = { key: unknown; value: boolean };

/** The property row ManagePropertiesView hands in, as stored. */
export type PropertyRow = {
  _id?: string;
  name?: string;
  description?: string;
  type?: string;
  order_listing?: number | string;
  order_product?: number | string;
  show?: SiteFlag[];
  box?: SiteFlag[];
};

export type UpdatePropertiesContainerProps = {
  propTpUpdate?: PropertyRow;
};

const upsertFlag = (
  arr: SiteFlag[] = [],
  key: unknown,
  value: boolean
): SiteFlag[] => {
  const without = (arr || []).filter((x) => x.key !== key);
  return [...without, { key, value }];
};

export default function UpdatePropertiesContainer({
  propTpUpdate,
}: UpdatePropertiesContainerProps) {
  const dispatch = useAppDispatch();

  // Store slices (read-only)
  const permission = useCurrentPermission();
  const currentWebsite = useAppSelector((s) => s.sites?.site || {});

  // Local editable draft (copy of incoming prop)
  const [draft, setDraft] = useState<PropertyRow>(propTpUpdate || { show: [], box: [] });
  const siteId = currentWebsite?._id;

  // Derived booleans for current site
  const siteShowChecked = useMemo(() => {
    const e = (draft.show || []).find((x) => x.key === siteId);
    return !!e?.value;
  }, [draft.show, siteId]);

  const siteBoxChecked = useMemo(() => {
    const e = (draft.box || []).find((x) => x.key === siteId);
    return !!e?.value;
  }, [draft.box, siteId]);

  const onChangeUpdateInput = useCallback((e) => {
    const { name, value } = e.target;
    setDraft((d) => ({ ...d, [name]: value }));
  }, []);

  const onChangeCheckboxShow = useCallback((e) => {
    const value = e.target.name === "show" ? e.target.checked : !!e.target.value;
    setDraft((d) => ({ ...d, show: upsertFlag(d.show, siteId, value) }));
  }, [siteId]);

  const onChangeCheckboxBox = useCallback((e) => {
    const value = e.target.name === "box" ? e.target.checked : !!e.target.value;
    setDraft((d) => ({ ...d, box: upsertFlag(d.box, siteId, value) }));
  }, [siteId]);

  const onClickUpdate = useCallback(() => {
    dispatch(updateProperty({ _id: draft._id, property: draft }));
    dispatch(clearMessage());
  }, [dispatch, draft]);

  
  const canEdit = !!(permission?.edit || permission?.publish);
  if (!canEdit) return null;

  return (
    <UpdatePropertiesView

      draft={draft}
      siteShowChecked={siteShowChecked}
      siteBoxChecked={siteBoxChecked}
      onChangeUpdateInput={onChangeUpdateInput}
      onChangeCheckboxShow={onChangeCheckboxShow}
      onChangeCheckboxBox={onChangeCheckboxBox}
      onClickUpdate={onClickUpdate}
    />
  );
}
